import React, { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
// Tabs removed — Browse and Search are now stacked sections
import {
  Archive,
  Search,
  FileText,
  FileSpreadsheet,
  FileCode2,
  Globe,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ExternalLink,
  Download,
  X,
  Clock,
  Hash,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { useArtifacts, useSources } from '@/hooks/useSources';
import { DocumentViewerModal } from '@/app/tools/_components/DocumentViewerModal';

// ── Helpers ───────────────────────────────────────────────────────────────────

function mimeIcon(mime: string) {
  if (mime.includes('pdf'))
    return <FileText className="w-4 h-4 text-red-500" />;
  if (mime.includes('spreadsheet') || mime.includes('excel'))
    return <FileSpreadsheet className="w-4 h-4 text-green-600" />;
  if (mime.includes('html') || mime.includes('xml'))
    return <FileCode2 className="w-4 h-4 text-blue-500" />;
  return <Globe className="w-4 h-4 text-gray-400" />;
}

function fmtBytes(n: number | null) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(s: string | null) {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ── Artifact detail drawer ────────────────────────────────────────────────────

import type { Artifact } from '@/hooks/useSources';

interface ArtifactDrawerProps {
  artifact: Artifact;
  hasPrev?: boolean;
  hasNext?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  onClose: () => void;
}

function ArtifactDrawer({
  artifact: a,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onClose,
}: ArtifactDrawerProps) {
  const [viewerOpen, setViewerOpen] = useState(false);
  const hasArchivedCopy = Boolean(a.storage_path);

  // Escape closes; Left/Right arrows navigate records
  React.useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && hasNext && onNext) onNext();
      if (e.key === 'ArrowLeft' && hasPrev && onPrev) onPrev();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, hasPrev, hasNext, onPrev, onNext]);

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative z-50 w-full max-w-lg bg-white shadow-2xl flex flex-col h-full overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2 min-w-0">
            {mimeIcon(a.mime_type ?? '')}
            <h2 className="font-semibold text-gray-900 truncate">
              {a.kind} — {a.url.split('/').pop()}
            </h2>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              size="icon"
              variant="ghost"
              onClick={onPrev}
              disabled={!hasPrev}
              title="Previous record (←)"
              aria-label="Previous"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={onNext}
              disabled={!hasNext}
              title="Next record (→)"
              aria-label="Next"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 px-5 py-4 space-y-5 text-sm">
          {/* Download / view */}
          <div className="flex gap-2">
            {hasArchivedCopy && (
              <Button
                onClick={() => setViewerOpen(true)}
                variant="outline"
                className="flex-1 gap-2"
              >
                <FileText className="w-4 h-4" />
                View document
              </Button>
            )}
            <Button
              onClick={() =>
                window.open(a.url, '_blank', 'noopener,noreferrer')
              }
              className="flex-1 gap-2"
            >
              <Download className="w-4 h-4" />
              Download original
            </Button>
          </div>

          {/* Provenance */}
          <section>
            <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
              Provenance
            </h3>
            <dl className="space-y-2">
              <Row label="URL">
                {hasArchivedCopy ? (
                  <button
                    type="button"
                    onClick={() => setViewerOpen(true)}
                    className="text-blue-600 hover:underline break-all text-left"
                  >
                    {a.url} <span className="text-gray-400">(archived)</span>
                  </button>
                ) : (
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline break-all"
                  >
                    {a.url}
                  </a>
                )}
              </Row>
              {a.discovered_on_url && (
                <Row label="Discovered on">
                  <span className="break-all text-gray-700">
                    {a.discovered_on_url}
                  </span>
                </Row>
              )}
              <Row label="Source">{a.sources?.name ?? '—'}</Row>
              {(a.department || a.meeting_type || a.meeting_date) && (
                <Row label="Classification">
                  <div className="space-y-0.5">
                    {a.department && (
                      <p className="text-gray-800">{a.department}</p>
                    )}
                    {a.meeting_type && (
                      <p className="text-gray-700 text-xs">{a.meeting_type}</p>
                    )}
                    {a.meeting_date && (
                      <p className="text-gray-600 text-xs">
                        {fmtDate(a.meeting_date)}
                      </p>
                    )}
                    {a.relevance_score != null && (
                      <p className="text-gray-500 text-xs">
                        Relevance: {(a.relevance_score * 100).toFixed(0)}%
                      </p>
                    )}
                  </div>
                </Row>
              )}
              <Row label="MIME type">{a.mime_type ?? '—'}</Row>
              <Row label="Size">{fmtBytes(a.byte_size)}</Row>
            </dl>
          </section>

          {/* Hash & versioning */}
          <section>
            <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
              Content identity
            </h3>
            <dl className="space-y-2">
              <Row label="SHA-256">
                <span className="font-mono text-xs break-all text-gray-800 flex items-center gap-1">
                  <Hash className="w-3 h-3 flex-shrink-0" />
                  {a.sha256}
                </span>
              </Row>
              {a.version_of && (
                <Row label="Version of">
                  <span className="font-mono text-xs text-gray-700">
                    {a.version_of}
                  </span>
                </Row>
              )}
            </dl>
          </section>

          {/* Timeline */}
          <section>
            <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
              Timeline
            </h3>
            <dl className="space-y-2">
              <Row label="First seen">
                <span className="flex items-center gap-1 text-gray-700">
                  <Clock className="w-3 h-3" />
                  {fmtDate(a.first_seen_at)}
                </span>
              </Row>
              <Row label="Last seen">
                <span className="flex items-center gap-1 text-gray-700">
                  <Clock className="w-3 h-3" />
                  {fmtDate(a.last_seen_at)}
                </span>
              </Row>
            </dl>
          </section>

          {/* Ingestion Status */}
          <section>
            <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
              Ingestion Status
            </h3>
            <dl className="space-y-2">
              <Row label="Extracted">
                {a.extracted_texts && a.extracted_texts.length > 0 ? (
                  <span className="flex items-center gap-1 text-green-700">
                    <CheckCircle2 className="w-4 h-4" />
                    Yes ({a.extracted_texts[0].status})
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-gray-600">
                    <XCircle className="w-4 h-4" />
                    No
                  </span>
                )}
              </Row>
              <Row label="Chunks">
                {a.chunks && a.chunks.length > 0 ? (
                  <span className="flex items-center gap-1 text-blue-700">
                    <CheckCircle2 className="w-4 h-4" />
                    {a.chunks.length} chunk{a.chunks.length !== 1 ? 's' : ''}
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-gray-600">
                    <XCircle className="w-4 h-4" />
                    None
                  </span>
                )}
              </Row>
              {a.failedJobs && a.failedJobs.length > 0 && (
                <Row label="Failures">
                  <div className="space-y-2">
                    {a.failedJobs.map((job, idx) => (
                      <div
                        key={idx}
                        className="bg-red-50 border border-red-200 rounded p-2"
                      >
                        <p className="text-xs font-medium text-red-900">
                          {job.job_type} ({job.status})
                        </p>
                        <p className="text-xs text-red-700 mt-1">{job.error}</p>
                        <p className="text-xs text-red-600 mt-1">
                          Attempts: {job.attempts}
                        </p>
                      </div>
                    ))}
                  </div>
                </Row>
              )}
            </dl>
          </section>
        </div>
      </div>
      {viewerOpen && hasArchivedCopy && (
        <DocumentViewerModal
          artifactId={a.id}
          sourceUrl={a.url}
          title={a.url.split('/').pop() ?? a.url}
          kind={a.kind}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-2">
      <dt className="w-28 flex-shrink-0 text-gray-600">{label}</dt>
      <dd className="flex-1 text-gray-900">{children}</dd>
    </div>
  );
}

// Known document kinds — covers what the archiver currently produces.
// "all" is a UI-only sentinel meaning no filter.
// Values must match the `kind` column written by the archiver.
const KIND_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'html_snapshot', label: 'HTML page' },
  { value: 'pdf', label: 'PDF' },
  { value: 'docx', label: 'Word (docx)' },
  { value: 'doc', label: 'Word (doc)' },
  { value: 'xlsx', label: 'Excel (xlsx)' },
  { value: 'xls', label: 'Excel (xls)' },
  { value: 'csv', label: 'CSV' },
  { value: 'audio', label: 'Audio' },
  { value: 'video', label: 'Video' },
  { value: 'image', label: 'Image' },
  { value: 'other', label: 'Other' },
];

// ── Archive browser ───────────────────────────────────────────────────────────

function ArtifactBrowser() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { sources } = useSources();

  // Initialise filters from URL params so links from other pages pre-filter
  const [sourceFilter, setSourceFilter] = useState<string>(
    searchParams.get('source') ?? 'all'
  );
  const [kindFilter, setKindFilter] = useState<string>(
    searchParams.get('kind') ?? 'all'
  );
  const [processingFilter, setProcessingFilter] = useState<string>(
    searchParams.get('processing') ?? 'all'
  );
  // Read-only deep-link filter (e.g. from Pipeline Reconcile "View" links).
  // No UI control to change it yet — clearing means removing the query param.
  const jurisdictionFilter = searchParams.get('jurisdiction') ?? undefined;
  const [urlInput, setUrlInput] = useState('');
  const [urlFilter, setUrlFilter] = useState('');
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep URL in sync when filters change
  function applySourceFilter(v: string) {
    setSourceFilter(v);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v === 'all') next.delete('source');
      else next.set('source', v);
      return next;
    });
  }

  function applyKindFilter(v: string) {
    setKindFilter(v);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v === 'all') next.delete('kind');
      else next.set('kind', v);
      return next;
    });
  }

  function applyProcessingFilter(v: string) {
    setProcessingFilter(v);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v === 'all') next.delete('processing');
      else next.set('processing', v);
      return next;
    });
  }

  function handleUrlChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    setUrlInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setUrlFilter(val.trim());
    }, 350);
  }

  function clearUrlFilter() {
    setUrlInput('');
    setUrlFilter('');
  }

  const { artifacts, loading, total, page, pageSize, loadArtifacts } =
    useArtifacts(
      sourceFilter === 'all' ? undefined : sourceFilter,
      urlFilter || undefined,
      kindFilter === 'all' ? undefined : kindFilter,
      processingFilter === 'all' ? undefined : processingFilter,
      jurisdictionFilter
    );
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const selected =
    selectedIdx !== null ? (artifacts[selectedIdx] ?? null) : null;

  const totalPages = Math.ceil(total / pageSize);

  // Navigate to adjacent record; cross page boundary when needed
  const navigateTo = useCallback(
    async (idx: number) => {
      const withinPage = idx >= 0 && idx < artifacts.length;
      if (withinPage) {
        setSelectedIdx(idx);
        return;
      }
      // Off the bottom of this page → load next page
      if (idx >= artifacts.length && page < totalPages - 1) {
        await loadArtifacts(
          page + 1,
          sourceFilter === 'all' ? undefined : sourceFilter,
          urlFilter || undefined,
          kindFilter === 'all' ? undefined : kindFilter,
          processingFilter === 'all' ? undefined : processingFilter
        );
        setSelectedIdx(0);
        return;
      }
      // Off the top of this page → load previous page
      if (idx < 0 && page > 0) {
        await loadArtifacts(
          page - 1,
          sourceFilter === 'all' ? undefined : sourceFilter,
          urlFilter || undefined,
          kindFilter === 'all' ? undefined : kindFilter,
          processingFilter === 'all' ? undefined : processingFilter
        );
        // select last item of the loaded page — pageSize gives us the expected count
        setSelectedIdx(pageSize - 1);
        return;
      }
    },
    [
      artifacts.length,
      page,
      totalPages,
      pageSize,
      loadArtifacts,
      sourceFilter,
      urlFilter,
      kindFilter,
      processingFilter,
    ]
  );

  // Reset selection when artifacts change (filter/page change outside of nav)
  const prevArtifactsRef = React.useRef(artifacts);
  React.useEffect(() => {
    if (prevArtifactsRef.current !== artifacts && selectedIdx === null) {
      prevArtifactsRef.current = artifacts;
    }
  }, [artifacts, selectedIdx]);

  return (
    <div className="space-y-4">
      {/* Artifact detail drawer */}
      {selected && (
        <ArtifactDrawer
          artifact={selected}
          hasPrev={selectedIdx !== null && (selectedIdx > 0 || page > 0)}
          hasNext={
            selectedIdx !== null &&
            (selectedIdx < artifacts.length - 1 || page < totalPages - 1)
          }
          onPrev={() =>
            selectedIdx !== null && void navigateTo(selectedIdx - 1)
          }
          onNext={() =>
            selectedIdx !== null && void navigateTo(selectedIdx + 1)
          }
          onClose={() => setSelectedIdx(null)}
        />
      )}
      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        {/* URL / filename search */}
        <div className="relative w-full sm:flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <Input
            placeholder="Filter by URL, department, or meeting type…"
            value={urlInput}
            onChange={handleUrlChange}
            className="pl-9 pr-8 bg-white text-gray-900 caret-gray-900 placeholder:text-gray-400"
          />
          {urlInput && (
            <button
              type="button"
              onClick={clearUrlFilter}
              onMouseDown={(e) => e.preventDefault()}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear filter"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {/* Source filter */}
        <div className="w-full sm:w-56 flex-shrink-0">
          <Select value={sourceFilter} onValueChange={applySourceFilter}>
            <SelectTrigger className="bg-white text-gray-900 border-gray-300">
              <SelectValue placeholder="All sources" />
            </SelectTrigger>
            <SelectContent className="bg-white text-gray-900">
              <SelectItem value="all">All sources</SelectItem>
              {sources.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Kind / document-type filter */}
        <div className="w-full sm:w-44 flex-shrink-0">
          <Select value={kindFilter} onValueChange={applyKindFilter}>
            <SelectTrigger className="bg-white text-gray-900 border-gray-300">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent className="bg-white text-gray-900">
              {KIND_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Processing status filter */}
        <div className="w-full sm:w-44 flex-shrink-0">
          <Select
            value={processingFilter}
            onValueChange={applyProcessingFilter}
          >
            <SelectTrigger className="bg-white text-gray-900 border-gray-300">
              <SelectValue placeholder="All artifacts" />
            </SelectTrigger>
            <SelectContent className="bg-white text-gray-900">
              <SelectItem value="all">All artifacts</SelectItem>
              <SelectItem value="processed">Processed</SelectItem>
              <SelectItem value="unprocessed">Unprocessed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <span className="text-sm text-gray-700 whitespace-nowrap font-medium">
          {total.toLocaleString()} artifact{total !== 1 ? 's' : ''}
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : artifacts.length === 0 ? (
        <div className="text-center py-16 text-gray-600">
          <Archive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="font-medium text-base">No artifacts yet</p>
          <p className="text-sm mt-1 text-gray-600">
            Run a scrape from the Sources page to ingest documents.
          </p>
        </div>
      ) : (
        <>
          {/* Pagination — top */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-gray-700">
              <span className="font-medium">
                Page {page + 1} of {totalPages} ({total.toLocaleString()} total)
              </span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page === 0}
                  className="bg-white text-gray-900 border-gray-300 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
                  onClick={() =>
                    void loadArtifacts(
                      page - 1,
                      sourceFilter === 'all' ? undefined : sourceFilter,
                      urlFilter || undefined,
                      kindFilter === 'all' ? undefined : kindFilter,
                      processingFilter === 'all' ? undefined : processingFilter
                    )
                  }
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= totalPages - 1}
                  className="bg-white text-gray-900 border-gray-300 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
                  onClick={() =>
                    void loadArtifacts(
                      page + 1,
                      sourceFilter === 'all' ? undefined : sourceFilter,
                      urlFilter || undefined,
                      kindFilter === 'all' ? undefined : kindFilter,
                      processingFilter === 'all' ? undefined : processingFilter
                    )
                  }
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden text-gray-900">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100 border-b-2 border-gray-300 hover:bg-gray-100 pointer-events-none">
                  <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                    Type
                  </TableHead>
                  <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                    URL
                  </TableHead>
                  <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                    Source / Classification
                  </TableHead>
                  <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                    Kind
                  </TableHead>
                  <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                    Status
                  </TableHead>
                  <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                    Size
                  </TableHead>
                  <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                    First seen
                  </TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {artifacts.map((a, idx) => (
                  <TableRow
                    key={a.id}
                    className={`cursor-pointer border-b border-gray-100 transition-colors ${
                      selectedIdx === idx
                        ? 'bg-blue-100 ring-1 ring-inset ring-blue-400'
                        : 'bg-white hover:bg-blue-50'
                    }`}
                    onClick={() => setSelectedIdx(idx)}
                  >
                    <TableCell>{mimeIcon(a.mime_type ?? '')}</TableCell>
                    <TableCell className="max-w-xs">
                      <p className="text-xs text-gray-900 truncate font-medium">
                        {a.url}
                      </p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-gray-800 font-medium leading-snug">
                        {a.sources?.name ?? '—'}
                      </p>
                      {(a.department || a.meeting_type) && (
                        <p className="text-xs text-gray-600 mt-0.5 leading-snug">
                          {[a.department, a.meeting_type]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className="capitalize text-gray-800 border-gray-300"
                      >
                        {a.kind}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <div title="Extracted">
                          {a.extracted_texts && a.extracted_texts.length > 0 ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-gray-300" />
                          )}
                        </div>
                        <div title="Chunked">
                          {a.chunks && a.chunks.length > 0 ? (
                            <CheckCircle2 className="w-4 h-4 text-blue-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-gray-300" />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-800">
                      {fmtBytes(a.byte_size)}
                    </TableCell>
                    <TableCell className="text-sm text-gray-800">
                      {fmtDate(a.first_seen_at)}
                    </TableCell>
                    <TableCell>
                      {a.storage_path ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedIdx(idx);
                          }}
                          className="text-blue-500 hover:text-blue-700"
                          title="View archived document"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                      ) : (
                        <a
                          href={a.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700"
                          title="Open original (external, may be dead)"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination — bottom */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-gray-700">
              <span className="font-medium">
                Page {page + 1} of {totalPages} ({total.toLocaleString()} total)
              </span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page === 0}
                  className="bg-white text-gray-900 border-gray-300 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
                  onClick={() =>
                    void loadArtifacts(
                      page - 1,
                      sourceFilter === 'all' ? undefined : sourceFilter,
                      urlFilter || undefined,
                      kindFilter === 'all' ? undefined : kindFilter,
                      processingFilter === 'all' ? undefined : processingFilter
                    )
                  }
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= totalPages - 1}
                  className="bg-white text-gray-900 border-gray-300 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
                  onClick={() =>
                    void loadArtifacts(
                      page + 1,
                      sourceFilter === 'all' ? undefined : sourceFilter,
                      urlFilter || undefined,
                      kindFilter === 'all' ? undefined : kindFilter,
                      processingFilter === 'all' ? undefined : processingFilter
                    )
                  }
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export const ArchivePage: React.FC = () => {
  usePageTitle('Artifacts DB');
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Archive className="w-7 h-7 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Artifacts DB</h1>
        </div>
        <p className="text-gray-700">
          Browse and search all ingested government documents and artifacts.
        </p>
      </div>

      <ArtifactBrowser />
    </div>
  );
};
